import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const AccountSettings = ({ user, onUpdateSettings }) => {
  const [activeSection, setActiveSection] = useState('profile');
  const [settings, setSettings] = useState({
    notifications: {
      email: user?.settings?.notifications?.email ?? true,
      push: user?.settings?.notifications?.push ?? true,
      achievements: user?.settings?.notifications?.achievements ?? true,
      missionReminders: user?.settings?.notifications?.missionReminders ?? false,
      weeklyProgress: user?.settings?.notifications?.weeklyProgress ?? true
    },
    privacy: {
      profileVisibility: user?.settings?.privacy?.profileVisibility ?? 'public',
      showProgress: user?.settings?.privacy?.showProgress ?? true,
      showAchievements: user?.settings?.privacy?.showAchievements ?? true
    },
    preferences: {
      theme: user?.settings?.preferences?.theme ?? 'dark',
      language: user?.settings?.preferences?.language ?? 'en',
      codeEditor: user?.settings?.preferences?.codeEditor ?? 'vs-dark',
      autoSave: user?.settings?.preferences?.autoSave ?? true
    }
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [twoFactorEnabled, setTwoFactorEnabled] = useState(user?.twoFactorEnabled || false);
  const [activeSessions] = useState([
    {
      id: 1,
      device: 'Chrome on Windows',
      location: 'New York, US',
      lastActive: '2025-01-04T18:30:00Z',
      current: true
    },
    {
      id: 2,
      device: 'Safari on iPhone',
      location: 'New York, US',
      lastActive: '2025-01-04T15:22:00Z',
      current: false
    },
    {
      id: 3,
      device: 'Firefox on Linux',
      location: 'San Francisco, US',
      lastActive: '2025-01-03T09:15:00Z',
      current: false
    }
  ]);

  const sections = [
    { id: 'profile', label: 'Profile Settings', icon: 'User' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'privacy', label: 'Privacy', icon: 'Shield' },
    { id: 'security', label: 'Security', icon: 'Lock' },
    { id: 'preferences', label: 'Preferences', icon: 'Settings' }
  ];

  const handleSettingChange = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev?.[category],
        [key]: value
      }
    }));
  };

  const handleSaveSettings = () => {
    onUpdateSettings(settings);
  };

  const handlePasswordChange = () => {
    if (passwordData?.newPassword !== passwordData?.confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    // Handle password change logic
    console.log('Password change requested');
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
  };

  const handleLogoutSession = (sessionId) => {
    console.log('Logout session:', sessionId);
  };

  const renderProfileSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Profile Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Display Name"
            type="text"
            value={user?.username}
            placeholder="Enter display name"
          />
          <Input
            label="Email Address"
            type="email"
            value={user?.email}
            placeholder="Enter email address"
          />
          <Input
            label="Location"
            type="text"
            value={user?.location || ''}
            placeholder="Enter your location"
          />
          <Input
            label="Website"
            type="url"
            value={user?.website || ''}
            placeholder="https://yourwebsite.com"
          />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Bio</h3>
        <textarea
          className="w-full p-3 bg-input border border-subtle rounded-lg text-foreground placeholder-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-ring"
          rows={4}
          placeholder="Tell us about yourself..."
          value={user?.bio || ''}
        />
      </div>

      <Button variant="default" onClick={handleSaveSettings}>
        Save Profile Changes
      </Button>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Email Notifications</h3>
        <div className="space-y-4">
          <Checkbox
            label="Email notifications"
            description="Receive general notifications via email"
            checked={settings?.notifications?.email}
            onChange={(e) => handleSettingChange('notifications', 'email', e?.target?.checked)}
          />
          <Checkbox
            label="Achievement notifications"
            description="Get notified when you earn new achievements"
            checked={settings?.notifications?.achievements}
            onChange={(e) => handleSettingChange('notifications', 'achievements', e?.target?.checked)}
          />
          <Checkbox
            label="Mission reminders"
            description="Receive reminders about incomplete missions"
            checked={settings?.notifications?.missionReminders}
            onChange={(e) => handleSettingChange('notifications', 'missionReminders', e?.target?.checked)}
          />
          <Checkbox
            label="Weekly progress reports"
            description="Get weekly summaries of your learning progress"
            checked={settings?.notifications?.weeklyProgress}
            onChange={(e) => handleSettingChange('notifications', 'weeklyProgress', e?.target?.checked)}
          />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Push Notifications</h3>
        <div className="space-y-4">
          <Checkbox
            label="Browser push notifications"
            description="Receive notifications in your browser"
            checked={settings?.notifications?.push}
            onChange={(e) => handleSettingChange('notifications', 'push', e?.target?.checked)}
          />
        </div>
      </div>

      <Button variant="default" onClick={handleSaveSettings}>
        Save Notification Settings
      </Button>
    </div>
  );

  const renderPrivacySettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Profile Visibility</h3>
        <div className="space-y-3">
          <label className="flex items-center space-x-3">
            <input
              type="radio"
              name="profileVisibility"
              value="public"
              checked={settings?.privacy?.profileVisibility === 'public'}
              onChange={(e) => handleSettingChange('privacy', 'profileVisibility', e?.target?.value)}
              className="w-4 h-4 text-primary bg-input border-subtle focus:ring-ring"
            />
            <div>
              <div className="font-medium text-foreground">Public</div>
              <div className="text-sm text-muted-foreground">Anyone can view your profile</div>
            </div>
          </label>
          <label className="flex items-center space-x-3">
            <input
              type="radio"
              name="profileVisibility"
              value="private"
              checked={settings?.privacy?.profileVisibility === 'private'}
              onChange={(e) => handleSettingChange('privacy', 'profileVisibility', e?.target?.value)}
              className="w-4 h-4 text-primary bg-input border-subtle focus:ring-ring"
            />
            <div>
              <div className="font-medium text-foreground">Private</div>
              <div className="text-sm text-muted-foreground">Only you can view your profile</div>
            </div>
          </label>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Data Sharing</h3>
        <div className="space-y-4">
          <Checkbox
            label="Show progress publicly"
            description="Allow others to see your learning progress"
            checked={settings?.privacy?.showProgress}
            onChange={(e) => handleSettingChange('privacy', 'showProgress', e?.target?.checked)}
          />
          <Checkbox
            label="Show achievements publicly"
            description="Display your earned achievements on your profile"
            checked={settings?.privacy?.showAchievements}
            onChange={(e) => handleSettingChange('privacy', 'showAchievements', e?.target?.checked)}
          />
        </div>
      </div>

      <Button variant="default" onClick={handleSaveSettings}>
        Save Privacy Settings
      </Button>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Change Password</h3>
        <div className="space-y-4 max-w-md">
          <Input
            label="Current Password"
            type="password"
            value={passwordData?.currentPassword}
            onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e?.target?.value })}
          />
          <Input
            label="New Password"
            type="password"
            value={passwordData?.newPassword}
            onChange={(e) => setPasswordData({ ...passwordData, newPassword: e?.target?.value })}
          />
          <Input
            label="Confirm New Password"
            type="password"
            value={passwordData?.confirmPassword}
            onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e?.target?.value })}
          />
          <Button variant="default" onClick={handlePasswordChange}>
            Update Password
          </Button>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Two-Factor Authentication</h3>
        <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
          <div>
            <div className="font-medium text-foreground">Two-Factor Authentication</div>
            <div className="text-sm text-muted-foreground">
              Add an extra layer of security to your account
            </div>
          </div>
          <Button
            variant={twoFactorEnabled ? "destructive" : "default"}
            size="sm"
            onClick={() => setTwoFactorEnabled(!twoFactorEnabled)}
          >
            {twoFactorEnabled ? 'Disable' : 'Enable'}
          </Button>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Active Sessions</h3>
        <div className="space-y-3">
          {activeSessions?.map((session) => (
            <div key={session?.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name="Monitor" size={20} color="var(--color-muted-foreground)" />
                <div>
                  <div className="font-medium text-foreground flex items-center gap-2">
                    {session?.device}
                    {session?.current && (
                      <span className="text-xs px-2 py-1 bg-success/20 text-success rounded-full">
                        Current
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {session?.location} • Last active {new Date(session.lastActive)?.toLocaleDateString()}
                  </div>
                </div>
              </div>
              {!session?.current && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleLogoutSession(session?.id)}
                >
                  Logout
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderPreferences = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Appearance</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Theme</label>
            <select
              value={settings?.preferences?.theme}
              onChange={(e) => handleSettingChange('preferences', 'theme', e?.target?.value)}
              className="px-3 py-2 bg-input border border-subtle rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="dark">Dark</option>
              <option value="light">Light</option>
              <option value="system">System</option>
            </select>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Code Editor</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Editor Theme</label>
            <select
              value={settings?.preferences?.codeEditor}
              onChange={(e) => handleSettingChange('preferences', 'codeEditor', e?.target?.value)}
              className="px-3 py-2 bg-input border border-subtle rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="vs-dark">VS Code Dark</option>
              <option value="vs-light">VS Code Light</option>
              <option value="monokai">Monokai</option>
              <option value="github">GitHub</option>
            </select>
          </div>
          <Checkbox
            label="Auto-save code"
            description="Automatically save your code changes"
            checked={settings?.preferences?.autoSave}
            onChange={(e) => handleSettingChange('preferences', 'autoSave', e?.target?.checked)}
          />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Language</h3>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Interface Language</label>
          <select
            value={settings?.preferences?.language}
            onChange={(e) => handleSettingChange('preferences', 'language', e?.target?.value)}
            className="px-3 py-2 bg-input border border-subtle rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="en">English</option>
            <option value="es">Spanish</option>
            <option value="fr">French</option>
            <option value="de">German</option>
          </select>
        </div>
      </div>

      <Button variant="default" onClick={handleSaveSettings}>
        Save Preferences
      </Button>
    </div>
  );

  return (
    <div className="bg-card rounded-lg border border-subtle overflow-hidden">
      <div className="flex flex-col lg:flex-row">
        {/* Settings Navigation */}
        <div className="lg:w-64 bg-muted/30 border-r border-subtle">
          <div className="p-4">
            <h2 className="text-lg font-semibold text-foreground mb-4">Account Settings</h2>
            <nav className="space-y-1">
              {sections?.map((section) => (
                <button
                  key={section?.id}
                  onClick={() => setActiveSection(section?.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                    activeSection === section?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted/50'
                  }`}
                >
                  <Icon name={section?.icon} size={16} />
                  {section?.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Settings Content */}
        <div className="flex-1 p-6">
          {activeSection === 'profile' && renderProfileSettings()}
          {activeSection === 'notifications' && renderNotificationSettings()}
          {activeSection === 'privacy' && renderPrivacySettings()}
          {activeSection === 'security' && renderSecuritySettings()}
          {activeSection === 'preferences' && renderPreferences()}
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;